import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "@hello-pangea/dnd";
import { Eye, EyeOff, Layers } from "lucide-react";
import React from "react";
import { ListChildComponentProps, VariableSizeList } from "react-window";
import { Shape } from "../../../../types";
import { IconButton } from "../../../ui/IconButton";
import { PanelShell } from "../../../ui/PanelShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import { useMapCanvasShapes } from "../../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorSelection
} from "../../contexts/MapEditorContext";
import { EditorPanelComponentProps } from "../EditorLayout/EditorLayoutContext";
import { PanelCollapseButton } from "../EditorLayout/PanelCollapseButton";
import { LayerItem } from "../layers/LayerItem";

type LayersPanelProps = EditorPanelComponentProps;

const LayerTabHeader: React.FC<{
  shapes: Shape[];
  updateShapes: (ids: string[], updatedShapes: Partial<Shape>) => void;
}> = ({ shapes, updateShapes }) => {
  const allHidden = shapes.length > 0 && shapes.every((s) => s.hidden);

  const handleToggleAll = () => {
    const ids = shapes.map((s) => s.id);
    updateShapes(ids, { hidden: !allHidden });
  };

  if (shapes.length === 0) return null;

  return (
    <div className="flex items-center justify-between border-neutral-200 border-b px-3 py-1.5 text-neutral-500 text-xs">
      <span>{shapes.length} items</span>
      <IconButton
        size="sm"
        label={allHidden ? "Show all" : "Hide all"}
        onClick={handleToggleAll}
      >
        {allHidden ? <EyeOff size={14} /> : <Eye size={14} />}
      </IconButton>
    </div>
  );
};

const ROW_HEIGHT = 32;

/** Group rows render their children nested inside the same LayerItem. */
const countRows = (shape: Shape): number => {
  if (shape.type === "group" && Array.isArray(shape.children)) {
    return (
      1 + shape.children.reduce((total, child) => total + countRows(child), 0)
    );
  }
  return 1;
};

interface LayerRowData {
  shapes: Shape[];
  selectedIds: string[];
  isMultiSelected: boolean;
  onLayerClick: (id: string, e: React.MouseEvent) => void;
}

/**
 * Row-level equality for the virtual list. `data.selectedIds` is a new
 * array on every selection change; comparing it by reference re-rendered
 * every visible row (each a dnd Draggable) on each click, so only the
 * row's own selected state, shape and edge position are compared.
 */
const areLayerRowsEqual = (
  prev: ListChildComponentProps<LayerRowData>,
  next: ListChildComponentProps<LayerRowData>
): boolean => {
  if (prev.index !== next.index || prev.style !== next.style) return false;
  const prevData = prev.data;
  const nextData = next.data;
  const prevShape = prevData.shapes[prev.index];
  const nextShape = nextData.shapes[next.index];
  if (prevShape !== nextShape) return false;
  if (prevData.shapes.length !== nextData.shapes.length) return false;
  if (prevData.isMultiSelected !== nextData.isMultiSelected) return false;
  if (prevData.onLayerClick !== nextData.onLayerClick) return false;
  return (
    prevData.selectedIds.includes(nextShape.id) ===
    nextData.selectedIds.includes(nextShape.id)
  );
};

/**
 * One virtualized row = one Draggable. Memoized so only visible rows exist
 * and only changed rows re-render — mounting a thousand dnd rows per tab
 * was what made tab switches take seconds.
 */
const VirtualLayerRow = React.memo<ListChildComponentProps<LayerRowData>>(
  ({ index, style, data }) => {
    const { shapes, selectedIds, isMultiSelected, onLayerClick } = data;
    const shape = shapes[index];

    const handleClick = React.useCallback(
      (e: React.MouseEvent) => onLayerClick(shape.id, e),
      [onLayerClick, shape.id]
    );

    return (
      <Draggable draggableId={shape.id} index={index}>
        {(dragProvided) => (
          <div
            ref={dragProvided.innerRef}
            {...dragProvided.draggableProps}
            style={{ ...style, ...dragProvided.draggableProps.style }}
          >
            <LayerItem
              shape={shape}
              isSelected={selectedIds.includes(shape.id)}
              isMultiSelected={isMultiSelected}
              onClick={handleClick}
              isFirst={index === 0}
              isLast={index === shapes.length - 1}
              dragHandleProps={dragProvided.dragHandleProps}
            />
          </div>
        )}
      </Draggable>
    );
  },
  areLayerRowsEqual
);
VirtualLayerRow.displayName = "VirtualLayerRow";

interface DraggableLayerListProps {
  droppableId: string;
  shapes: Shape[];
  selectedIds: string[];
  isMultiSelected: boolean;
  onLayerClick: (id: string, e: React.MouseEvent) => void;
  onDragEnd: (result: DropResult) => void;
}

const DraggableLayerList: React.FC<DraggableLayerListProps> = ({
  droppableId,
  shapes,
  selectedIds,
  isMultiSelected,
  onLayerClick,
  onDragEnd
}) => {
  const listRef = React.useRef<VariableSizeList>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = React.useState(0);

  // The list fills whatever space the tab gives it — measured because
  // react-window needs a concrete pixel height.
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setListHeight(el.clientHeight));
    observer.observe(el);
    setListHeight(el.clientHeight);
    return () => observer.disconnect();
  }, []);

  // Display order is reversed (visually-top items at the bottom of the
  // shapes array).
  const reversedShapes = React.useMemo(() => [...shapes].reverse(), [shapes]);

  // Row heights depend on group nesting — invalidate the size cache when
  // the list changes.
  React.useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [reversedShapes]);

  // Reveal the selected row. Off-screen rows don't exist in a virtual
  // list, so scrolling has to go through the list API rather than the
  // row's own scrollIntoView.
  const firstSelectedId = selectedIds[0];
  React.useEffect(() => {
    if (!firstSelectedId) return;
    const index = reversedShapes.findIndex(
      (shape) => shape.id === firstSelectedId
    );
    if (index >= 0) listRef.current?.scrollToItem(index, "smart");
  }, [firstSelectedId, reversedShapes]);

  const itemData = React.useMemo<LayerRowData>(
    () => ({
      shapes: reversedShapes,
      selectedIds,
      isMultiSelected,
      onLayerClick
    }),
    [reversedShapes, selectedIds, isMultiSelected, onLayerClick]
  );

  return (
    <div ref={containerRef} className="min-h-0 flex-1">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={droppableId}
          mode="virtual"
          renderClone={(dragProvided, _snapshot, rubric) => (
            <div
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              {...dragProvided.dragHandleProps}
              className="rounded bg-blue-50 shadow-md"
            >
              <LayerItem
                shape={reversedShapes[rubric.source.index]}
                isSelected={false}
                isMultiSelected={false}
                onClick={() => {}}
              />
            </div>
          )}
        >
          {(provided) => (
            <VariableSizeList
              ref={listRef}
              height={listHeight}
              width="100%"
              itemCount={reversedShapes.length}
              itemSize={(index) =>
                ROW_HEIGHT * countRows(reversedShapes[index])
              }
              itemData={itemData}
              itemKey={(index, data) => data.shapes[index].id}
              outerRef={provided.innerRef}
              overscanCount={8}
            >
              {VirtualLayerRow}
            </VariableSizeList>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export const LayersPanel: React.FC<LayersPanelProps> = ({ side }) => {
  const { selectedIds } = useMapEditorSelection();
  const { setSelectedIds, updateShapes, setShapes } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();

  // Stable identity (reads the current selection through a ref) so the
  // memoized rows don't all re-render whenever the selection changes.
  const selectedIdsRef = React.useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const handleLayerClick = React.useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();

      const currentSelectedIds = selectedIdsRef.current;
      if (e.shiftKey) {
        const newSelectedIds = currentSelectedIds.includes(id)
          ? currentSelectedIds.filter((selectedId) => selectedId !== id)
          : [...currentSelectedIds, id];
        setSelectedIds(newSelectedIds);
      } else {
        setSelectedIds([id]);
      }
    },
    [setSelectedIds]
  );

  const { regularShapes, spotShapes, floorShapes } = React.useMemo(() => {
    const regular: Shape[] = [];
    const spots: Shape[] = [];
    // Uploaded artwork — floor plans and backgrounds — lives in one list.
    const floor: Shape[] = [];

    const collectSpots = (items: Shape[]) => {
      for (const shape of items) {
        if (shape.layerType === "spots") {
          spots.push(shape);
        } else if (shape.type === "group" && Array.isArray(shape.children)) {
          collectSpots(shape.children);
        }
      }
    };

    for (const shape of shapes) {
      if (shape.layerType === "spots") continue;
      // Zone regions are managed from the Zones panel, not as shapes.
      if (shape.type === "zone") continue;
      // Uploaded artwork (SVG floor plans, background images) belongs to
      // the Floor tab; every drawn shape belongs to Shapes — including
      // rectangles that legacy SVG imports classified as "floor".
      if (shape.type === "svg" || shape.type === "image") {
        floor.push(shape);
      } else if (
        shape.layerType === "shapes" ||
        shape.layerType === "floor" ||
        shape.layerType === "background"
      ) {
        regular.push(shape);
      }
    }

    collectSpots(shapes);

    return {
      regularShapes: regular,
      spotShapes: spots,
      floorShapes: floor
    };
  }, [shapes]);

  const isMultiSelected = selectedIds.length > 1;

  // Selecting a shape on the canvas reveals it in the panel — including
  // switching to the tab that actually lists it (spots live under
  // Resources, artwork under Backgrounds, everything else under Shapes).
  const [activeTab, setActiveTab] = React.useState("resources");

  React.useEffect(() => {
    const firstSelectedId = selectedIds[0];
    if (!firstSelectedId) return;
    if (spotShapes.some((shape) => shape.id === firstSelectedId)) {
      setActiveTab("resources");
    } else if (floorShapes.some((shape) => shape.id === firstSelectedId)) {
      setActiveTab("floor");
    } else if (regularShapes.some((shape) => shape.id === firstSelectedId)) {
      setActiveTab("shapes");
    }
  }, [selectedIds, spotShapes, floorShapes, regularShapes]);

  const handleDragEnd = React.useCallback(
    (layerShapes: Shape[]) => (result: DropResult) => {
      if (!result.destination) return;
      const srcIndex = result.source.index;
      const destIndex = result.destination.index;
      if (srcIndex === destIndex) return;

      // The list is displayed in reverse order (visually-top items at bottom),
      // so map visual indices back to the original array order.
      const reversed = [...layerShapes].reverse();
      const movedShape = reversed[srcIndex];
      const targetShape = reversed[destIndex];

      setShapes((prev) => {
        const newShapes = [...prev];
        const fromIdx = newShapes.findIndex((s) => s.id === movedShape.id);
        const toIdx = newShapes.findIndex((s) => s.id === targetShape.id);
        if (fromIdx === -1 || toIdx === -1) return prev;

        const [removed] = newShapes.splice(fromIdx, 1);
        newShapes.splice(toIdx, 0, removed);
        return newShapes;
      });
    },
    [setShapes]
  );

  const tabsScrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <PanelShell
      title={"Layers"}
      icon={Layers}
      actions={<PanelCollapseButton side={side} />}
      // Shares the column with the Zones panel: take what is left rather
      // than the full height, or a collapsed Zones panel gets pushed out
      // of view below.
      className="h-auto min-h-0 flex-1"
    >
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex h-full w-full select-none flex-col"
      >
        <div
          ref={tabsScrollRef}
          className="shrink-0 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          <TabsList className="inline-flex w-max gap-x-1 px-2 pt-1">
            <TabsTrigger
              value="resources"
              className="flex-shrink-0 px-2.5 py-1.5 text-xs"
            >
              Resources
            </TabsTrigger>
            <TabsTrigger
              value="shapes"
              className="flex-shrink-0 px-2.5 py-1.5 text-xs"
            >
              Shapes
            </TabsTrigger>
            <TabsTrigger
              value="floor"
              className="flex-shrink-0 px-2.5 py-1.5 text-xs"
            >
              Backgrounds
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent
          value="shapes"
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <LayerTabHeader shapes={regularShapes} updateShapes={updateShapes} />
          {regularShapes.length === 0 ? (
            <div className="p-4 text-center text-neutral-500 text-sm">
              No shapes yet
            </div>
          ) : (
            <DraggableLayerList
              droppableId="shapes-layer"
              shapes={regularShapes}
              selectedIds={selectedIds}
              isMultiSelected={isMultiSelected}
              onLayerClick={handleLayerClick}
              onDragEnd={handleDragEnd(regularShapes)}
            />
          )}
        </TabsContent>
        <TabsContent
          value="floor"
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <LayerTabHeader shapes={floorShapes} updateShapes={updateShapes} />
          {floorShapes.length === 0 ? (
            <div className="p-4 text-center text-neutral-500 text-sm">
              No floor plan or background yet — upload one from the top toolbar
            </div>
          ) : (
            <DraggableLayerList
              droppableId="floor-layer"
              shapes={floorShapes}
              selectedIds={selectedIds}
              isMultiSelected={isMultiSelected}
              onLayerClick={handleLayerClick}
              onDragEnd={handleDragEnd(floorShapes)}
            />
          )}
        </TabsContent>
        <TabsContent
          value="resources"
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <LayerTabHeader shapes={spotShapes} updateShapes={updateShapes} />
          {spotShapes.length === 0 ? (
            <div className="p-4 text-center text-neutral-500 text-sm">
              No resources yet
            </div>
          ) : (
            <DraggableLayerList
              droppableId="resources-layer"
              shapes={spotShapes}
              selectedIds={selectedIds}
              isMultiSelected={isMultiSelected}
              onLayerClick={handleLayerClick}
              onDragEnd={handleDragEnd(spotShapes)}
            />
          )}
        </TabsContent>
      </Tabs>
    </PanelShell>
  );
};
